import React, { useEffect, useRef, useState } from "react";
import { FiX, FiPlus, FiSend } from "react-icons/fi";
import FushiMessage from "./FushiMessage";
import FushiMascot from "./FushiMascot";

// The chat window itself — presentational. All state comes in via props.
const FushiPanel = ({ messages, isLoading, onSend, onNewChat, onClose }) => {
  const [text, setText] = useState("");
  const endRef = useRef(null);

  // Keep the latest message (and the typing indicator) in view.
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages, isLoading]);

  const handleSend = () => {
    const trimmed = text.trim();
    if (!trimmed || isLoading) return;
    onSend(trimmed);
    setText("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div
      className="fushi-panel fixed z-[70] flex flex-col overflow-hidden rounded-2xl font-poppins text-text-primary
                 left-3 right-3 bottom-[82px] h-[70dvh] max-h-[75dvh]
                 tablet:left-auto tablet:right-6 tablet:w-[370px] tablet:bottom-[90px] tablet:h-[540px] tablet:max-h-[75dvh]"
      role="dialog"
      aria-label="Chat with Fushi"
    >
      {/* Header */}
      <header className="fushi-header flex items-center justify-between px-4 py-3">
        <div className="flex items-center gap-2.5">
          <div className="relative flex h-9 w-9 items-center justify-center">
            <FushiMascot
              size={36}
              outlineColor="#ffffff"
              faceColor="transparent"
              featureColor="#ffffff"
              shineColor="#0f9c5a"
            />
            <span className="fushi-online-dot" />
          </div>
          <div className="leading-tight">
            <p className="text-[15px] font-semibold text-white">Fushi</p>
            <p className="text-[11px] text-white/80">Online</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={onNewChat}
            aria-label="Start a new chat"
            title="New chat"
            className="rounded-full p-2 text-white/85 transition-colors hover:bg-white/20 hover:text-white"
          >
            <FiPlus size={18} />
          </button>
          <button
            onClick={onClose}
            aria-label="Close chat"
            title="Close"
            className="rounded-full p-2 text-white/85 transition-colors hover:bg-white/20 hover:text-white"
          >
            <FiX size={18} />
          </button>
        </div>
      </header>

      {/* Messages */}
      <div className="fushi-messages scrollbar-hide flex-1 space-y-2.5 overflow-y-auto px-3 py-3">
        {messages.map((m) => (
          <FushiMessage key={m.id} message={m} />
        ))}

        {isLoading && (
          <div className="fushi-msg-row flex justify-start">
            <div className="fushi-bubble-bot flex items-center gap-1 rounded-2xl rounded-bl-md px-3.5 py-3.5">
              <span className="fushi-dot" />
              <span className="fushi-dot" />
              <span className="fushi-dot" />
            </div>
          </div>
        )}

        <div ref={endRef} />
      </div>

      {/* Composer */}
      <div className="flex items-end gap-2 border-t border-white/10 bg-bg-dark/60 p-2.5">
        <textarea
          rows={1}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Message Fushi…"
          className="fushi-input scrollbar-hide max-h-24 flex-1 resize-none rounded-xl px-3.5 py-2.5 text-[14px] text-text-primary placeholder:text-text-secondary focus:outline-none"
        />
        <button
          onClick={handleSend}
          disabled={isLoading || !text.trim()}
          aria-label="Send message"
          className="fushi-send flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-white"
        >
          <FiSend size={17} />
        </button>
      </div>
    </div>
  );
};

export default FushiPanel;
